import _ from 'lodash';
import { resize, ResizeAPI, ResizeMode } from '@wix/thumbnail-generator';
import * as util from '#packages/util';
import constants from '#packages/constants';
import * as mediaManagerPanelConstants from '../mediaManagerPanelConstants/mediaManagerPanelConstants';
import * as addComponentData from './mediaManagerPanelAddCompData';
import { translate } from '#packages/i18n';

import type {
  CompStructure,
  StyleRef,
  StyleRefs,
} from 'types/documentServices';

const { url } = util;
const mediaManagerMediaTypes = constants.MEDIA_MANAGER_MEDIA_TYPES;
const {
  SEARCH_SECTIONS_TITLES_MAP,
  SECTIONS_TITLES_MAP,
  MEDIA_SOURCES,
  MEDIA_MANAGER_UPLOAD_SOURCES,
  TRANSCODING_STATUSES,
  PUBLIC_MEDIA_ROOT,
  MEDIA_MANAGER_OPEN_SOURCE_BASE,
  PANEL_BI_SOURCE_SUFFIX,
  MEDIA_MANAGER_PANEL_SECTION_ID,
  MEDIA_PRODUCER,
  SHUTTERSTOCK_MEDIA_TYPE,
} = mediaManagerPanelConstants;

const DEFAUTL_VIDEO_RATIO = 1.777;
const DEFAULT_PICTURE_RATIO = 1;

type MediaItem = AnyFixMe; // none of the existing typeof of MM items seem to fully fit the type of properties used in this file.
type MediaType =
  (typeof mediaManagerMediaTypes)[keyof typeof mediaManagerMediaTypes];
type MediaItemSource = (typeof MEDIA_SOURCES)[keyof typeof MEDIA_SOURCES];
type MediaSection = Record<'items', MediaItem[]>;
interface MediaCategory {
  id: string;
  subFolders: AnyFixMe;
}

interface ComponentStructureCreatorOptions {
  isSocialIcon?: boolean;
  isResponsiveEditor?: boolean;
}

const getItemMediaBaseHost = (itemSource: MediaItemSource) => {
  switch (itemSource) {
    case MEDIA_SOURCES.PRIVATE:
    case MEDIA_SOURCES.PUBLIC:
      return 'https://static.wixstatic.com';
    case MEDIA_SOURCES.SHUTTERSTOCK:
    default:
      return '';
  }
};

const isShutterStockItem = (item: MediaItem) =>
  item.source === MEDIA_SOURCES.SHUTTERSTOCK;

const isWixMediaItem = (item: MediaItem) =>
  item.source === MEDIA_SOURCES.PUBLIC;

const isPrivateMediaItem = (item: MediaItem) =>
  item.source === MEDIA_SOURCES.PRIVATE;

const isPicture = (item: MediaItem) =>
  item.mediaType === mediaManagerMediaTypes.PICTURE;

const getResizedPrivateMediaPicture = (
  itemSource: MediaItemSource,
  pictureUrl: string,
  width: number,
  height: number,
  mode: ResizeMode,
) =>
  url.joinURL(
    getItemMediaBaseHost(itemSource),
    resize(pictureUrl, { width, height, mode }, ResizeAPI.Vanbrandt),
  );

const generatePicturePreviewUrl = (
  item: MediaItem,
  width: number,
  height: number,
  mode: ResizeMode,
) => {
  if (isShutterStockItem(item)) {
    return item.previewUrl;
  }

  return getResizedPrivateMediaPicture(
    item.source,
    item.fileUrl,
    width,
    height,
    mode,
  );
};

const generatePictureThumbnailUrl = (
  item: MediaItem,
  width: number,
  height: number,
  mode: ResizeMode,
) => {
  if (isShutterStockItem(item)) {
    return item.thumbnailUrl;
  }

  if (isWixMediaItem(item) && isWixMediaFilePaid(item)) {
    return item.thumbnailUrl;
  }

  const thumbnailUrl = getResizedPrivateMediaPicture(
    item.source,
    item.fileUrl,
    width,
    height,
    mode,
  );

  const isWebp = thumbnailUrl.match(/webp$/i);

  if (isWebp && util.browserUtil.isSafari()) {
    return `${thumbnailUrl}.png`;
  }

  return thumbnailUrl;
};

const getVideoThumbnailUrl = (
  item: MediaItem,
  width: number,
  height: number,
  mode: ResizeMode,
) => {
  const { thumbnailUrl } = item;

  if (isShutterStockItem(item)) {
    return thumbnailUrl;
  }

  const images = item?.fileOutput?.image ?? [];

  const privateMediaImageUrl = images.length
    ? images[images.length - 1].url || thumbnailUrl
    : thumbnailUrl;

  return privateMediaImageUrl === thumbnailUrl
    ? thumbnailUrl
    : getResizedPrivateMediaPicture(
        item.source,
        privateMediaImageUrl,
        width,
        height,
        mode,
      );
};

const getRiveThumbnailUrl = (
  item: MediaItem,
  width: number,
  height: number,
  mode: ResizeMode,
) => {
  const thumbnailUrl = item?.fileOutput?.preview[0].url ?? '';
  if (thumbnailUrl.length) {
    return getResizedPrivateMediaPicture(
      item.source,
      thumbnailUrl,
      width,
      height,
      mode,
    );
  }
  return thumbnailUrl;
};

const getExtension = (str: string) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/split
  _(str || '')
    .split('.')
    .last();

const getPrivateMediaFileExtension = (item: MediaItem) => {
  const { fileUrl } = item;
  const possibleExtension = getExtension(fileUrl);

  return possibleExtension === fileUrl ? '' : possibleExtension;
};

const getWixMediaFileExtension = (item: MediaItem) =>
  isVideoItem(item) ? 'mp4' : getExtension(item.fileName);

const getShutterstockFileExtension = (item: MediaItem) =>
  isVideoItem(item) ? 'mp4' : 'jpg';

const getFileExtension = (item: MediaItem) => {
  if (isPrivateMediaItem(item)) {
    return getPrivateMediaFileExtension(item);
  }

  if (isWixMediaItem(item)) {
    return getWixMediaFileExtension(item);
  }

  return getShutterstockFileExtension(item);
};

const isItemOfType = (item: MediaItem, mediaType: MediaType) =>
  item.mediaType === mediaType;

const isItemOneOfTypes = (item: MediaItem, mediaTypes: MediaType[]) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/includes
  _.includes(mediaTypes, item.mediaType);

const appendZeroIfFigureSmallerTen = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const getReadableDuration = (duration: number) => {
  const seconds = Math.trunc(duration / 1000);
  const sec = seconds % 60;
  let min = Math.trunc(seconds / 60);
  let hours = 0;

  if (min > 60) {
    hours = Math.trunc(min / 60);
    min %= 60;
  }

  return `${
    hours ? `${appendZeroIfFigureSmallerTen(hours)}:` : ''
  }${appendZeroIfFigureSmallerTen(min)}:${appendZeroIfFigureSmallerTen(sec)}`;
};

const getDefaultPreviewRatio = (item: MediaItem) =>
  isItemOfType(item, mediaManagerMediaTypes.VIDEO)
    ? DEFAUTL_VIDEO_RATIO
    : DEFAULT_PICTURE_RATIO;

const getPreviewRatio = (item: MediaItem) =>
  (isShutterStockItem(item)
    ? item?.fileInfo?.aspect
    : item.width / item.height) || getDefaultPreviewRatio(item);

const getDesiredValue = (value: number, minValue: number, maxValue: number) => {
  const minMaxValue = Math.min(value, maxValue);

  return minMaxValue < minValue ? minMaxValue : Math.max(minMaxValue, minValue);
};

const getPreviewDimension = (
  item: MediaItem,
  minWidth: number,
  maxWidth: number,
  minHeight: number,
  maxHeight: number,
) => {
  const ratio = getPreviewRatio(item);
  const desiredWidth =
    isShutterStockItem(item) || !item.width
      ? maxWidth
      : getDesiredValue(item.width, minWidth, maxWidth);
  const desiredHeight =
    isShutterStockItem(item) || !item.height
      ? maxHeight
      : getDesiredValue(item.height, minHeight, maxHeight);

  let probablyDimension =
    ratio >= 1
      ? {
          width: desiredWidth,
          height: Math.round(desiredWidth / ratio),
        }
      : {
          width: Math.round(desiredHeight * ratio),
          height: desiredHeight,
        };

  if (probablyDimension.width > maxWidth) {
    probablyDimension = {
      width: maxWidth,
      height: Math.round(maxWidth / ratio),
    };
  }

  return probablyDimension;
};

const isRecentlyAddedSection = (sectionId: string) =>
  sectionId ===
  mediaManagerPanelConstants.MEDIA_MANAGER_PANEL_SECTION_ID.SITE_FILES;

const timeNow = () => Date.now();

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line you-dont-need-lodash-underscore/replace
const filterFileUrl = (str: string) => _.replace(str, /.*\//, '');

const filterDomain = (str: string) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/replace
  _.replace(str, /(http:\/\/[^\/]+\/)/i, '');

const getBestQualityTrack = ({ audio }: { audio: any[] }) =>
  (audio || [])
    .filter((track) => track.format === 'mp3')
    .sort((track1, track2) => (track1.key > track2.key ? -1 : 1))[0];

const prepareItemBeforeAddComponent = (item: MediaItem) => {
  const {
    id,
    mediaType,
    name,
    width,
    height,
    fileInput,
    fileOutput,
    embedHtml,
    externalSources,
    labels,
    tags,
    fileUrl = '',
  } = item;

  let fileOutputUrl = fileUrl;
  if (isItemOfType(item, mediaManagerMediaTypes.MUSIC)) {
    const bestQualityTrack = getBestQualityTrack(fileOutput || {});
    fileOutputUrl = bestQualityTrack?.url || fileUrl;
  }

  const mapped: MediaItem = {
    id,
    mediaType,
    fileName: filterFileUrl(fileOutputUrl),
    uri: filterFileUrl(fileOutputUrl), // for TPA-SDK
    fileUrl: fileOutputUrl,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/slice
    title: _.slice(name, 0, 40).join(''),
    fileInput,
    fileOutput,
    labels,
    tags,
  };

  if (
    isItemOneOfTypes(item, [
      mediaManagerMediaTypes.PICTURE,
      mediaManagerMediaTypes.VIDEO,
      mediaManagerMediaTypes.SWF,
    ])
  ) {
    mapped.width = width;
    mapped.height = height;
  }

  if (isItemOfType(item, mediaManagerMediaTypes.VIDEO)) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/is-undefined
    mapped.embedHtml = _.isUndefined(embedHtml) ? null : embedHtml;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/is-undefined
    mapped.externalSources = _.isUndefined(externalSources)
      ? null
      : externalSources;
    mapped.fileBaseUrl = fileOutputUrl.split('/').slice(0, 2).join('/');
  }

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
  _.forEach(['thumbnailUrl', 'previewUrl', 'source'], (attrName) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/is-undefined
    if (!_.isUndefined(mapped[attrName])) {
      return;
    }

    mapped[attrName] = /Url/i.test(attrName)
      ? filterDomain(item[attrName])
      : item[attrName];
  });

  return mapped;
};

const isAddMediaSection = (sectionId: string) =>
  sectionId === MEDIA_MANAGER_PANEL_SECTION_ID.ADD_MEDIA;

const isRecommendedMediaCard = (sectionId: string) =>
  sectionId === MEDIA_MANAGER_PANEL_SECTION_ID.RECOMMENDED_MEDIA_CARD;

const isRecommendedMediaSection = (sectionId: string) =>
  sectionId === MEDIA_MANAGER_PANEL_SECTION_ID.RECOMMENDED_MEDIA;

const isCreateMediaSection = (sectionId: string) =>
  sectionId === MEDIA_MANAGER_PANEL_SECTION_ID.CREATE_MEDIA;

const isUploadAddMediaSource = (source: string) =>
  source === MEDIA_MANAGER_UPLOAD_SOURCES.COMPUTER;

const isVideoFileTranscodingFailed = (item: MediaItem) => {
  const { transcodingStatus } = item;

  return transcodingStatus === TRANSCODING_STATUSES.FAILED;
};

const isVideoFileTranscodedSuccessfully = (item: MediaItem) => {
  const { transcodingStatus } = item;

  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/is-undefined
    _.isUndefined(transcodingStatus) ||
    transcodingStatus === TRANSCODING_STATUSES.READY
  );
};

const isVideoFileTranscodingInProgress = (item: MediaItem) => {
  const { transcodingStatus } = item;

  return (
    Boolean(transcodingStatus) &&
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    !_.includes(
      [TRANSCODING_STATUSES.READY, TRANSCODING_STATUSES.FAILED],
      transcodingStatus,
    )
  );
};

const canShowPreviewForItem = (item: MediaItem) =>
  isItemOneOfTypes(item, [
    mediaManagerMediaTypes.PICTURE,
    mediaManagerMediaTypes.SHAPE,
    mediaManagerMediaTypes.VIDEO,
  ]);

const isVideoItem = (item: MediaItem) =>
  item.mediaType === mediaManagerMediaTypes.VIDEO;

const getNthOrFirstItem = <T>(array: T[], index = 0) =>
  array[index] ? array[index] : _.head(array);

const getTopVisibleSectionIndex = (
  sectionsDimensionIntervals: number[][],
  containerScrollTop: number,
  containerHeight: number,
) => {
  let navigationSelectedSectionIndex = 0;

  if (containerScrollTop > 0) {
    const isBottomReached =
      containerScrollTop + containerHeight >=
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/last
      _.last(sectionsDimensionIntervals)[1];

    navigationSelectedSectionIndex = isBottomReached
      ? sectionsDimensionIntervals.length - 1
      : // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line you-dont-need-lodash-underscore/reduce
        _.reduce(
          sectionsDimensionIntervals,
          (resultIndex, gap, index) =>
            gap[0] <= containerScrollTop && gap[1] > containerScrollTop
              ? index
              : resultIndex,
          0,
        );
  }

  return navigationSelectedSectionIndex;
};

const transformSectionsDOMInstancesToDimensionIntervals = (
  instances: HTMLElement[],
) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(instances, (instance) => {
    const instanceOffsetTop = Math.floor(instance.offsetTop);

    return [
      instanceOffsetTop,
      instanceOffsetTop +
        Math.floor(instance.offsetHeight) +
        parseInt(window.getComputedStyle(instance).marginBottom, 10),
    ];
  });

const shiftDOMInstanceIntervals = (intervals: number[][], shiftSize: number) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(intervals, ([intervalStart, intervalEnd]) => [
    intervalStart - shiftSize,
    intervalEnd - shiftSize,
  ]);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line you-dont-need-lodash-underscore/to-lower
const sectionIdToBiField = (sectionId: string) => _.toLower(sectionId);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line you-dont-need-lodash-underscore/join
const tagsToString = (tags: string[] = []) => _.join(tags, ',');

const getComponentStructureCreator = (
  item: MediaItem,
  options: ComponentStructureCreatorOptions = {},
) => {
  const { isSocialIcon } = options;

  if (isSocialIcon) {
    return addComponentData.socialIcon;
  }

  if (getPrivateMediaFileExtension(item) === 'riv') {
    return addComponentData.riveAnimation;
  }

  return addComponentData[item.mediaType as keyof typeof addComponentData];
};

const getComposedComponentStructureCreator = ({
  isSocialIcon,
}: ComponentStructureCreatorOptions = {}) => {
  if (isSocialIcon) {
    return addComponentData.socialBar;
  }

  return null;
};

const isSocialIconsRoot = (root: string) =>
  root === PUBLIC_MEDIA_ROOT.SOCIAL_ICONS;

const _getAllSubcategoriesList = (category: MediaCategory): string[] => {
  const { subFolders } = category;

  return subFolders?.length
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/reduce
      _.reduce(
        subFolders,
        (subCategoriesList, subCategory) => [
          ...subCategoriesList,
          ..._getAllSubcategoriesList(subCategory),
        ],
        [],
      )
    : [category.id];
};

const mapPublicMediaSubcategoryToRoot = (tree: AnyFixMe) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/reduce
  _.reduce(
    tree,
    (rootsMap, rootCategory) => {
      const { id: publicMediaRoot } = rootCategory;
      const subCategoriesList = _getAllSubcategoriesList(rootCategory);

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/assign
      return _.assign(
        {},
        rootsMap,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line you-dont-need-lodash-underscore/reduce
        _.reduce(
          subCategoriesList,
          (subcategoriesMap, subCategory) =>
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line you-dont-need-lodash-underscore/assign
            _.assign({}, subcategoriesMap, { [subCategory]: publicMediaRoot }),
          {},
        ),
      );
    },
    {},
  );

const _extractFolderIdFromSearchPath = (chunks: string[]) => {
  const source = _.nth(chunks, 1);

  switch (source) {
    case MEDIA_SOURCES.PRIVATE:
      return _.nth(chunks, 3) || '';
    case MEDIA_SOURCES.PUBLIC:
      return _.nth(chunks, 4);
    default:
      return '';
  }
};

const extractFolderIdFromPath = (path: string) => {
  if (!path) {
    return '';
  }

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/split
  const chunks = _.split(path, '/');
  const probablySource = _.head(chunks);

  if (probablySource === 'search') {
    return _extractFolderIdFromSearchPath(chunks);
  }

  switch (probablySource) {
    case MEDIA_SOURCES.PRIVATE:
      return _.nth(chunks, 1) || '';
    case MEDIA_SOURCES.PUBLIC:
      return _.nth(chunks, 2);
    default:
      return '';
  }
};

const generateMediaManagerOpenSource = (
  sectionId: string,
  isSearching = false,
) =>
  `${MEDIA_MANAGER_OPEN_SOURCE_BASE}_${PANEL_BI_SOURCE_SUFFIX[sectionId]}${
    isSearching ? '_search' : ''
  }`;

const generateMediaManagerAddMediaSectionOpenSource = (source: string) =>
  `${MEDIA_MANAGER_OPEN_SOURCE_BASE}_add_media_section_${source}`;

const findFirstParentButtonNode = (childNode: HTMLElement) => {
  const _findButtonNode = (node: HTMLElement): HTMLElement => {
    if (!node || !node.tagName) {
      return null;
    }

    return node.tagName.toLowerCase() === 'button'
      ? node
      : _findButtonNode(node.parentNode as HTMLElement);
  };

  return _findButtonNode(childNode);
};

const getComponentStructureSkin = (structure: CompStructure) =>
  (structure?.style as StyleRef | StyleRefs)?.skin ||
  structure.skin ||
  structure.style;

const isWixMediaFilePaid = (item: MediaItem) => {
  const systemTags = item.systemTags || [];

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/includes
  return _.includes(systemTags, 'paid');
};

const selectExpandedCategories = (
  selectedCategoriesIds: string[],
  categories: MediaCategory[],
) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(categories, (category) =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    _.includes(selectedCategoriesIds, category.id) // eslint-disable-next-line you-dont-need-lodash-underscore/assign
      ? _.assign({}, category, { isSelected: true })
      : category,
  );

/**
 *
 * @param {Array} sections
 * @param predicate
 * @returns {*|LodashWrapper|Object}
 */
const filterOutSectionsItemsIds = (
  sections: MediaSection[],
  predicate = (_item: MediaItem) => true,
) => _(sections).flatMap('items').compact().filter(predicate).map('id').value();

const sectionsListToObj = (sections: MediaSection[]) => _.keyBy(sections, 'id');

const isSiteMediaTokenFromTemplateSite = (siteMediaToken: string) => {
  try {
    const { metasiteId } = JSON.parse(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/split
      JSON.parse(window.atob(_.split(siteMediaToken, '.')[2])).data,
    );

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    return _.includes(metasiteId, '_template_');
  } catch (e) {
    return false;
  }
};

const getMediaProducer = (item: MediaItem) =>
  isWixMediaItem(item) ? MEDIA_PRODUCER.WIX : MEDIA_PRODUCER.SHUTTERSTOCK;

const generateBiSectionId = (sectionId: string, item: MediaItem) => {
  if (!_.isEqual(sectionId, MEDIA_MANAGER_PANEL_SECTION_ID.SITE_FILES)) {
    return sectionId;
  }

  const tags = item?.tags ?? [];
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/includes
  const isShutterstockFileOrigin = _.includes(tags, '_shutterstock');
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/includes
  const isWixstockFileOrigin = _.includes(tags, '_wixstock');

  let fileOrigin = '';

  if (isShutterstockFileOrigin) {
    fileOrigin = MEDIA_PRODUCER.SHUTTERSTOCK;
  } else if (isWixstockFileOrigin) {
    fileOrigin = MEDIA_PRODUCER.WIX;
  }

  return fileOrigin ? `${fileOrigin}_${item.mediaType}` : sectionId;
};

const generateMediaManagerOpenPathFromUrlParam = (param: string) => {
  const { OPEN_BY_URL_SOURCE } = mediaManagerPanelConstants;

  switch (param) {
    case OPEN_BY_URL_SOURCE.SITE_FILES:
      return MEDIA_SOURCES.PRIVATE;
    case OPEN_BY_URL_SOURCE.WIX_IMAGES:
      return `${MEDIA_SOURCES.PUBLIC}`;
    case OPEN_BY_URL_SOURCE.WIX_VIDEOS:
      return `${MEDIA_SOURCES.PUBLIC}/8e256233-1752-4026-9341-54036e7f875e`;
    case OPEN_BY_URL_SOURCE.WIX_EXCLUSIVES_VIDEOS:
      return `${MEDIA_SOURCES.PUBLIC}/8e256233-1752-4026-9341-54036e7f875e/834e0f2d-9836-40c8-a73a-2a4627a84ca2`;
    case OPEN_BY_URL_SOURCE.SHUTTERSTOCK_IMAGES:
      return `${MEDIA_SOURCES.SHUTTERSTOCK}/${SHUTTERSTOCK_MEDIA_TYPE.IMAGE}`;
    case OPEN_BY_URL_SOURCE.SHUTTERSTOCK_VIDEOS:
      return `${MEDIA_SOURCES.SHUTTERSTOCK}/${SHUTTERSTOCK_MEDIA_TYPE.VIDEO}`;
    case OPEN_BY_URL_SOURCE.UNSPLASH:
      return `${MEDIA_SOURCES.UNSPLASH}`;
    default:
      return MEDIA_SOURCES.PRIVATE;
  }
};

const getMediaManagerSectionIdBasedOnUrlParam = (param: string) => {
  const { OPEN_BY_URL_SOURCE } = mediaManagerPanelConstants;

  switch (param) {
    case OPEN_BY_URL_SOURCE.SITE_FILES:
      return MEDIA_MANAGER_PANEL_SECTION_ID.SITE_FILES;
    case OPEN_BY_URL_SOURCE.WIX_IMAGES:
      return MEDIA_MANAGER_PANEL_SECTION_ID.WIX_IMAGES;
    case OPEN_BY_URL_SOURCE.WIX_VIDEOS:
      return MEDIA_MANAGER_PANEL_SECTION_ID.WIX_VIDEOS;
    case OPEN_BY_URL_SOURCE.SHUTTERSTOCK_IMAGES:
      return MEDIA_MANAGER_PANEL_SECTION_ID.SHUTTERSTOCK_IMAGES;
    case OPEN_BY_URL_SOURCE.SHUTTERSTOCK_VIDEOS:
      return MEDIA_MANAGER_PANEL_SECTION_ID.SHUTTERSTOCK_VIDEOS;
    case OPEN_BY_URL_SOURCE.UNSPLASH:
      return MEDIA_MANAGER_PANEL_SECTION_ID.SITE_FILES; // TODO: update to unsplash section id, when unsplash section will be ready
    default:
      return MEDIA_MANAGER_PANEL_SECTION_ID.SITE_FILES;
  }
};

const createEmptySection = (
  isSearching: boolean,
  sectionId: string,
  options = {},
) => {
  const titlesMap = isSearching
    ? SEARCH_SECTIONS_TITLES_MAP
    : SECTIONS_TITLES_MAP;

  return {
    id: sectionId,
    title: translate(titlesMap[sectionId]),
    items: [] as AnyFixMe,
    action: null as AnyFixMe,
    shouldDisplayPresetName: false,
    presetWidth: 90,
    options,
  };
};

export {
  isRecentlyAddedSection,
  isItemOfType,
  isItemOneOfTypes,
  isPicture,
  isSocialIconsRoot,
  generatePictureThumbnailUrl,
  generatePicturePreviewUrl,
  getVideoThumbnailUrl,
  getRiveThumbnailUrl,
  getPrivateMediaFileExtension,
  getReadableDuration,
  getPreviewRatio,
  getPreviewDimension,
  timeNow,
  prepareItemBeforeAddComponent,
  isAddMediaSection,
  isRecommendedMediaCard,
  isRecommendedMediaSection,
  isCreateMediaSection,
  isUploadAddMediaSource,
  canShowPreviewForItem,
  isVideoFileTranscodingFailed,
  isVideoFileTranscodedSuccessfully,
  isVideoFileTranscodingInProgress,
  getNthOrFirstItem,
  getTopVisibleSectionIndex,
  transformSectionsDOMInstancesToDimensionIntervals,
  shiftDOMInstanceIntervals,
  sectionIdToBiField,
  tagsToString,
  getComponentStructureCreator,
  mapPublicMediaSubcategoryToRoot,
  extractFolderIdFromPath,
  getComposedComponentStructureCreator,
  generateMediaManagerOpenSource,
  generateMediaManagerAddMediaSectionOpenSource,
  findFirstParentButtonNode,
  getComponentStructureSkin,
  isWixMediaFilePaid,
  isWixMediaItem,
  isShutterStockItem,
  selectExpandedCategories,
  addComponentData,
  filterOutSectionsItemsIds,
  isVideoItem,
  sectionsListToObj,
  isSiteMediaTokenFromTemplateSite,
  getMediaProducer,
  getFileExtension,
  generateBiSectionId,
  generateMediaManagerOpenPathFromUrlParam,
  getMediaManagerSectionIdBasedOnUrlParam,
  createEmptySection,
};
